import { storeToRefs } from 'pinia';
import { readonly, ref } from 'vue';
import { useApp } from '@/libraries/composables/useApp';
import { usePlatformActions } from '@/libraries/composables/usePlatformActions';
import { getDepositUrl } from '@/libraries/url';
import { loadScript } from '@/libraries/utils/dom';
import { useProductPlatformStore } from '@/stores/productPlatformStore';

// Global state (file scope) - shared across all component instances
const isQuickDepositLoaded = ref(false);
let quickDepositLoadPromise: Promise<void> | null = null;

async function initializeQuickDeposit() {
    try {
        if (!window.QuickDepositPlugin) {
            await loadScript(__QUICK_DEPOSIT_RESOURCE_URL__);
        }
        await window.ensureQuickDepositPluginReady();
    } catch (error) {
        console.error('Failed to initialize quick deposit:', error);
    }
    isQuickDepositLoaded.value = true;
}

export function useQuickDeposit() {
    const { openDepositPageInApp } = useApp();
    const { handleActionByPlatform } = usePlatformActions();
    const { isComponentInPp } = storeToRefs(useProductPlatformStore());

    function goToDeposit() {
        window.location.href = getDepositUrl();
    }

    async function handleDeposit() {
        try {
            await window.ensureQuickDepositPluginReady();
            await window.QuickDepositPlugin!.open({
                productType: isComponentInPp.value ? 'Landing' : 'Sports',
                pageName: isComponentInPp.value ? 'Landing' : 'SPLobby',
            });
        } catch (error) {
            console.error('quick deposit error', error);
            goToDeposit();
        }
    }

    async function clickDeposit() {
        await handleActionByPlatform({
            appAction: () => openDepositPageInApp(),
            webAction: async () => {
                await handleDeposit();
            },
        });
    }

    function loadQuickDepositScript(): Promise<void> {
        if (isQuickDepositLoaded.value) {
            return Promise.resolve();
        }
        quickDepositLoadPromise ??= initializeQuickDeposit().finally(() => {
            quickDepositLoadPromise = null;
        });

        return quickDepositLoadPromise;
    }

    return {
        isQuickDepositLoaded: readonly(isQuickDepositLoaded),
        loadQuickDepositScript,
        handleDeposit,
        clickDeposit,
    };
}
